import { useEffect, useMemo, useRef, useState } from 'react';
import { listKbDocuments } from '../api/backend';
import { DOC_STATUS, DOC_TYPE, PUBLISHED, chipText } from '../utils/chipContract';

const STATUS_VARIANTS = {
  draft: 'muted',
  pending: 'info',
  processing: 'warning',
  indexed: 'muted',
  error: 'error',
  archived: 'muted',
};

const STATUS_OPTIONS = [
  { value: '', label: 'Все статусы' },
  { value: 'draft', label: chipText(DOC_STATUS.draft) },
  { value: 'pending', label: chipText(DOC_STATUS.pending) },
  { value: 'processing', label: chipText(DOC_STATUS.processing) },
  { value: 'indexed', label: chipText(DOC_STATUS.indexed) },
  { value: 'error', label: chipText(DOC_STATUS.error) },
  { value: 'archived', label: chipText(DOC_STATUS.archived) },
];

const TYPE_OPTIONS = [
  { value: '', label: 'Все типы' },
  { value: 'lecture', label: chipText(DOC_TYPE.lecture) },
  { value: 'methodical', label: chipText(DOC_TYPE.methodical) },
  { value: 'faq', label: chipText(DOC_TYPE.faq) },
  { value: 'instruction', label: chipText(DOC_TYPE.instruction) },
  { value: 'glossary', label: chipText(DOC_TYPE.glossary) },
  { value: 'example', label: chipText(DOC_TYPE.example) },
  { value: 'external', label: chipText(DOC_TYPE.external) },
];

const PUBLISHED_OPTIONS = [
  { value: '', label: 'Все состояния' },
  { value: 'yes', label: `${PUBLISHED.emoji} опубликован` },
  { value: 'no', label: 'не опубликован' },
];

function StatusBadge({ status }) {
  const variant = STATUS_VARIANTS[status] || 'muted';
  const chip = DOC_STATUS[status];
  return (
    <span
      className={`ai-status ai-status--${variant} ai-status--emoji ai-status--icon`}
      title={chip ? `Статус документа: ${chip.label}` : status}
    >
      {chip ? chip.emoji : status}
    </span>
  );
}

function KbDocumentList({ filters, onFiltersChange, selectedDocument, onSelectDocument, refreshTick }) {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const PAGE_SIZE = 7;
  const listRef = useRef(null);
  const pendingListFocusRef = useRef(false);

  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'ArrowDown' && e.key !== 'ArrowUp') return;
      const t = e.target;
      if (t && (t.closest('input') || t.closest('textarea') || t.closest('select') || t.isContentEditable)) {
        return;
      }
      if (!filteredDocuments.length) return;
      const absIdx = selectedDocument
        ? filteredDocuments.findIndex((d) => String(d.id) === String(selectedDocument.id))
        : 0;
      if (absIdx < 0) return;
      const nextAbsIdx =
        e.key === 'ArrowDown'
          ? Math.min(filteredDocuments.length - 1, absIdx + 1)
          : Math.max(0, absIdx - 1);
      if (nextAbsIdx === absIdx) return;
      e.preventDefault();
      const next = filteredDocuments[nextAbsIdx];
      if (!next?.id) return;
      pendingListFocusRef.current = true;
      setPage(Math.floor(nextAbsIdx / PAGE_SIZE) + 1);
      onSelectDocument(next);
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDocument, filters.search, filters.status, filters.document_type, filters.published]);

  async function load() {
    setLoading(true);
    setError(null);
    try {
      // The backend list endpoint has no status/document_type filters
      // (only course/module/is_published) — status and type are filtered
      // client-side below, like search.
      const data = await listKbDocuments({ limit: 500 });
      setDocuments(data);
      setPage(1);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const filteredDocuments = useMemo(() => {
    let list = documents;
    const published = filters.published || '';
    if (published === 'yes') list = list.filter((doc) => doc.is_published);
    else if (published === 'no') list = list.filter((doc) => !doc.is_published);
    if (filters.status) list = list.filter((doc) => doc.status === filters.status);
    if (filters.document_type) {
      list = list.filter((doc) => doc.document_type === filters.document_type);
    }
    const search = (filters.search || '').toLowerCase();
    if (!search) return list;
    return list.filter((doc) =>
      [doc.title, doc.document_type, doc.status, String(doc.id)]
        .filter(Boolean)
        .some((field) => field.toLowerCase().includes(search))
    );
  }, [documents, filters.search, filters.published, filters.status, filters.document_type]);

  const totalPages = Math.max(1, Math.ceil(filteredDocuments.length / PAGE_SIZE));
  const paginatedDocuments = filteredDocuments.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshTick]);

  // Client-side filters: no refetch, just back to the first page.
  useEffect(() => {
    setPage(1);
  }, [filters.status, filters.document_type, filters.published]);

  // Auto-focus on first document when list loads and nothing is selected
  useEffect(() => {
    if (!selectedDocument && paginatedDocuments.length > 0) {
      onSelectDocument(paginatedDocuments[0]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paginatedDocuments.map((d) => d.id).join(','), selectedDocument?.id]);

  useEffect(() => {
    if (!selectedDocument?.id) return;
    const list = listRef.current;
    if (!list) return;
    const safeId =
      typeof CSS !== 'undefined' && typeof CSS.escape === 'function'
        ? CSS.escape(String(selectedDocument.id))
        : String(selectedDocument.id).replace(/"/g, '\\"');
    const row = list.querySelector(`[data-doc-id="${safeId}"]`);
    if (!row) return;
    row.scrollIntoView({ block: 'nearest' });
    const listHasFocus = document.activeElement instanceof Node && list.contains(document.activeElement);
    const shouldFocus = pendingListFocusRef.current || listHasFocus;
    pendingListFocusRef.current = false;
    if (!shouldFocus) return;
    const id = window.requestAnimationFrame(() => {
      row.focus({ preventScroll: true });
    });
    return () => window.cancelAnimationFrame(id);
  }, [selectedDocument?.id, page]);

  const handleSearchChange = (event) => {
    onFiltersChange({ ...filters, search: event.target.value });
  };

  if (loading) {
    return (
      <div className="ai-card flex h-full items-center justify-center p-4">
        <span className="mr-2 inline-block animate-pulse">●</span>
        Загрузка документов…
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-card h-full p-4">
        <div className="ai-error text-sm">{error}</div>
      </div>
    );
  }

  return (
    <div className="ai-card flex h-full flex-col overflow-hidden p-3">
      {/* Filters */}
      <div className="flex flex-wrap items-center gap-2 mb-2">
        <select
          value={filters.status || ''}
          onChange={(event) => onFiltersChange({ ...filters, status: event.target.value })}
          className="ai-select w-auto min-w-[130px]"
        >
          {STATUS_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={filters.document_type || ''}
          onChange={(event) => onFiltersChange({ ...filters, document_type: event.target.value })}
          className="ai-select w-auto min-w-[130px]"
        >
          {TYPE_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
        <select
          value={filters.published || ''}
          onChange={(event) => onFiltersChange({ ...filters, published: event.target.value })}
          className="ai-select w-auto min-w-[130px]"
          aria-label="Состояние публикации"
        >
          {PUBLISHED_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>{option.label}</option>
          ))}
        </select>
      </div>

      {/* Search */}
      <input
        type="text"
        value={filters.search || ''}
        onChange={handleSearchChange}
        placeholder="Поиск по имени файла, статусу, версии..."
        className="ai-input w-full mb-2"
      />

      {/* Pagination on top */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between border-b border-ai-border pb-2 mb-2 text-xs">
          <button
            onClick={() => setPage((p) => Math.max(1, p - 1))}
            disabled={page === 1}
            className="ai-btn-outline px-2 py-1"
            type="button"
          >
            ← Назад
          </button>
          <span className="text-ai-text-secondary">
            Страница {page} из {totalPages}
          </span>
          <button
            onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
            disabled={page === totalPages}
            className="ai-btn-outline px-2 py-1"
            type="button"
          >
            Вперёд →
          </button>
        </div>
      )}

      {/* Document list */}
      <div className="flex-1 overflow-y-auto pr-1 min-h-0" ref={listRef}>
        {paginatedDocuments.length === 0 ? (
          <div className="ai-empty py-8">Документы не найдены.</div>
        ) : (
          <div className="flex flex-col gap-2">
            {paginatedDocuments.map((doc) => {
              const activeVersion = doc.versions?.find((v) => v.id === doc.active_version_id);
              const typeChip = DOC_TYPE[doc.document_type];
              const isSelected = selectedDocument?.id === doc.id;
              return (
                <button
                  key={doc.id}
                  data-doc-id={doc.id}
                  onClick={() => onSelectDocument(doc)}
                  className={`text-left rounded-ai border p-2 transition-colors ${
                    isSelected
                      ? 'border-ai-primary bg-ai-primary-light'
                      : 'border-ai-border bg-ai-surface hover:border-ai-primary'
                  }`}
                  type="button"
                >
                  {/* Row 1: date + publication + status.
                      Size is set on the row (owner rule: one line = one font
                      size) so the chip inherits the date's 12px, not the
                      card's base size. */}
                  <div className="flex items-center justify-between gap-2 mb-1 text-xs">
                    <span className="text-ai-text-muted truncate">
                      {new Date(doc.created_at).toLocaleString('ru-RU')}
                    </span>
                    <span className="flex items-center gap-1 flex-shrink-0">
                      {doc.is_published && (
                        <span className="ai-status ai-status--signal--success ai-status--emoji ai-status--icon" title="Публикация: Опубликован">
                          📤
                        </span>
                      )}
                      <StatusBadge status={doc.status} />
                    </span>
                  </div>
                  {/* Row 2: file name, prefixed by the doc-type emoji
                      (type is known — it identifies the item at a glance). */}
                  <div className="text-sm font-medium text-ai-text line-clamp-2 mb-1">
                    {typeChip && (
                      <span
                        className="mr-1 flex-shrink-0"
                        title={`${typeChip.emoji} ${typeChip.label}`}
                      >
                        {typeChip.emoji}
                      </span>
                    )}
                    {doc.title}
                  </div>
                  {/* Row 3: version / chunks / embedding */}
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-ai-text-secondary">
                    <span>v{activeVersion?.version_number || '?'}</span>
                    <span>·</span>
                    <span>{activeVersion?.chunk_count || 0} чанков</span>
                    <span>·</span>
                    <span className="truncate max-w-[120px]">
                      {activeVersion?.embedding_model || '—'}
                    </span>
                  </div>
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

export default KbDocumentList;
